import { GET_ONBOARDING_V2_VARIANT } from '@/components/constants/flags';
import { iframeSafeAuthRoute } from '@/components/modal/sign-in-modal/utils';
import { appendAmplitudeCrossDomainParams } from '@/utils/amplitudeCrossDomain';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { prefixWithLocale } from '@/utils/localeRedirect';
import { AUTH0_LOGIN_PATH } from '@heidi/auth/contracts';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ScreenHint } from '@heidi/schema/ml-scribe';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { SandPatternBackground, setV2Eligible } from '@heidi/growth';
import { useOnboardingLocaleSync } from '../hooks/useOnboardingLocaleSync';
import { useTimezoneLanguageDefault } from '../hooks/useTimezoneLanguageDefault';

/**
 * Sanitizes a `?post_login_redirect_url` query value before forwarding it to
 * Auth0's `returnTo` parameter. Closes the open-redirect attack surface
 * (`?post_login_redirect_url=https://evil.com` / `//evil.com` / etc.) while
 * preserving the deep-link query params that legitimate consumers depend on
 * (e.g. the template-share flow's `?from=shared-template`).
 *
 * Stricter than `getSafeRedirectUrl` from `@/lib/navigation-allowlist`, which
 * only permits the exact `/scribe` pathname and strips all query params
 * except `action` and `utm_*`. The bypass needs `/scribe/new-user` (and its
 * deep-link query) to round-trip intact, so we apply a narrower set of
 * checks: relative path, under `/scribe/*`, no protocol-relative or
 * backslash tricks. Any rejected input falls back to `/scribe/new-user`.
 */
function getSafePostLoginRedirect(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }
  try {
    const parsed = new URL(value, 'http://localhost');
    if (parsed.pathname !== '/scribe' && !parsed.pathname.startsWith('/scribe/')) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

/**
 * Renders in place of the in-app sign-up surface (V2 `<SignUpPage>` or legacy
 * `<UnauthenticatedDemoFlow>`) when `release-onboarding-signup-bypass` is
 * enabled. Fires the same side effects `SignUpPage` + `SignUpForm`'s email
 * continue handler perform before they send the user to Auth0, then redirects
 * to the Auth0 register screen directly — skipping the in-app form entirely.
 *
 * Auth0-only by design. The bypass exists to onboard new marketing signups
 * onto Auth0 (the vendor the rest of the app is migrating to) rather than
 * Kinde. We can't call `/determine-login` without an email, so we don't try —
 * we route every bypass entry straight to `/auth/login?screen_hint=signup`,
 * which is where the per-email DetermineLogin lookup would send a net-new
 * email anyway. If Auth0 routing turns out to be wrong for a given
 * environment, turn off the `release-onboarding-signup-bypass` flag —
 * traffic falls back to `<SignUpPage>` and the existing per-email
 * DetermineLogin lookup picks Kinde or Auth0 correctly.
 *
 * Marketing CTAs are the only intended entry point and don't append any
 * special query params today. SSO (`?sso_org=`) and mobile webview
 * (`?source=ios|android` + `?loginHint=`) entries primarily target the
 * legacy login surface at `/` (root) — `<SignInModal>` handles those flows
 * with the per-vendor DetermineLogin lookup. We don't try to replicate them
 * here.
 *
 * Notable side effects that must run before the redirect:
 *  - `setV2Eligible()` so `/scribe/new-user` renders the V2
 *    `<PersonalizationFlow>` after auth (not the legacy `<OnboardingModal>`).
 *  - `?post_login_redirect_url` is honored as the final post-auth destination.
 *  - The redirect URL is wrapped with attribution + Amplitude cross-domain
 *    params so the funnel survives the Auth0 roundtrip.
 *  - A `screen_viewed` event tagged `signup_bypass` is fired once so the
 *    funnel keeps a step parallel to `signup_full_page_view`.
 */
export function RedirectToAuthSignup() {
  const router = useRouter();
  // Same source `SignUpForm` uses for `ui_locales` — see useTimezoneLanguageDefault.
  const { locale } = useIntl();

  // useTimezoneLanguageDefault return `undefined` (no-op) when an explicit locale is already
  // active — the timezone must not override the URL/browser language.
  useOnboardingLocaleSync(useTimezoneLanguageDefault());

  // Variant is stable per session — snapshot once at mount so the analytics
  // event tags the variant the user actually saw the bypass under.
  const [onboardingVariant] = useState(() => GET_ONBOARDING_V2_VARIANT());
  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (!router.isReady || hasTrackedScreenView.current) return;
    hasTrackedScreenView.current = true;
    trackScreenViewed({
      screen_name: ScreenName.SignupBypass,
      url: router.asPath,
      onboarding_variant: onboardingVariant,
    });
  }, [router.isReady, router.asPath, onboardingVariant]);

  // Trigger the Auth0 register redirect once after the router hydrates so we
  // have the final query params. Guarded with a ref so router re-renders
  // can't double-fire the navigation.
  const hasRedirected = useRef(false);
  useEffect(() => {
    if (!router.isReady || hasRedirected.current) return;
    hasRedirected.current = true;

    setV2Eligible();

    const basePath =
      getSafePostLoginRedirect(router.query.post_login_redirect_url) ??
      '/scribe/new-user';
    const postSignupRedirectUrl = appendAmplitudeCrossDomainParams(
      appendAttributionParamsToRedirectUrl(prefixWithLocale(locale, basePath))
    );

    const params = new URLSearchParams();
    params.set('returnTo', postSignupRedirectUrl);
    params.set('screen_hint', ScreenHint.signup);
    if (locale) params.set('ui_locales', toAuth0UiLocales(locale));
    iframeSafeAuthRoute(`${AUTH0_LOGIN_PATH}?${params.toString()}`);
  }, [router.isReady, router.query.post_login_redirect_url, locale]);

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden">
      <SandPatternBackground />
      <div className="size-8 animate-spin rounded-full border-4 border-border border-t-text-primary" />
    </div>
  );
}
